export class ReportRunManager {
    constructor(ChartLibrary) {
        this.ChartLibrary = ChartLibrary;
        this.chart = null;
        this.refreshTimer = null;
        this.root = null;
    }

    initialize(root = document) {
        const runRoot = root.matches?.('[data-report-run-root]')
            ? root
            : root.querySelector?.('[data-report-run-root]');

        if (!runRoot) {
            return;
        }

        this.destroyWithin(runRoot);
        this.root = runRoot;
        this.cacheElements();
        this.bindEvents();
        this.loadConfiguration();
        this.loadReport();
        this.startRefreshTimer();
    }

    cacheElements() {
        this.canvas = this.root.querySelector('[data-report-run-canvas]');
        this.table = this.root.querySelector('[data-report-run-table]');
        this.visual = this.root.querySelector('[data-report-run-visual]');
        this.meta = this.root.querySelector('[data-report-run-meta]');
        this.empty = this.root.querySelector('[data-report-run-empty]');
        this.error = this.root.querySelector('[data-report-run-error]');
        this.type = this.root.querySelector('[data-report-run-type]');
        this.refreshButton = this.root.querySelector('[data-report-run-refresh]');
        this.configStatus = this.root.querySelector('[data-report-run-config-status]');
        this.configBody = this.root.querySelector('[data-report-run-config-body]');
        this.configFields = {
            title: this.root.querySelector('[data-report-config-title]'),
            reportType: this.root.querySelector('[data-report-config-report-type]'),
            chartType: this.root.querySelector('[data-report-config-chart-type]'),
            tableName: this.root.querySelector('[data-report-config-table-name]'),
            filterQuery: this.root.querySelector('[data-report-config-filter-query]'),
            columns: this.root.querySelector('[data-report-config-columns]'),
            groupByField: this.root.querySelector('[data-report-config-group-by-field]'),
            aggregateType: this.root.querySelector('[data-report-config-aggregate-type]'),
            aggregateField: this.root.querySelector('[data-report-config-aggregate-field]'),
            rowLimit: this.root.querySelector('[data-report-config-row-limit]'),
            drilldownEnabled: this.root.querySelector('[data-report-config-drilldown-enabled]'),
            isActive: this.root.querySelector('[data-report-config-is-active]'),
            optionsJson: this.root.querySelector('[data-report-config-options-json]')
        };
    }

    async loadConfiguration() {
        const reportId = this.root?.dataset?.reportId;
        if (!reportId) {
            this.setConfigurationError('Report configuration is unavailable.');
            return;
        }

        this.setConfigurationLoading('Loading report configuration...');

        try {
            const response = await fetch(`/api/reports/${reportId}/builder`, {
                headers: { Accept: 'application/json' },
                credentials: 'same-origin'
            });
            const payload = await response.json();
            if (!response.ok) {
                throw new Error(payload.error || 'Unable to load report configuration');
            }

            this.renderConfiguration(payload.report || {});
        } catch (error) {
            console.error('Report configuration load failed', error);
            this.setConfigurationError(error.message || 'Unable to load report configuration');
        }
    }

    renderConfiguration(report) {
        const setText = (element, value) => {
            if (element) {
                element.textContent = value;
            }
        };

        setText(this.configFields.title, report.title || 'Untitled report');
        setText(this.configFields.reportType, this.humanize(report.report_type || ''));
        setText(this.configFields.chartType, report.report_type === 'chart' ? this.humanize(report.chart_type || 'bar') : 'Not applicable');
        setText(this.configFields.tableName, report.table_name || 'Not set');
        setText(this.configFields.filterQuery, report.filter_query || 'No filter query');
        setText(this.configFields.columns, report.columns || 'No explicit columns');
        setText(this.configFields.groupByField, report.group_by_field || 'Not set');
        setText(this.configFields.aggregateType, report.aggregate_type ? this.humanize(report.aggregate_type) : 'Not set');
        setText(this.configFields.aggregateField, report.aggregate_field || 'Not set');
        setText(this.configFields.rowLimit, report.row_limit ? String(report.row_limit) : 'Default');
        setText(this.configFields.drilldownEnabled, report.drilldown_enabled === false ? 'Disabled' : 'Enabled');
        setText(this.configFields.isActive, report.is_active === false ? 'Inactive' : 'Active');
        setText(this.configFields.optionsJson, this.prettyJson(report.options_json || '{}'));

        this.configStatus?.classList.add('hidden');
        this.configBody?.classList.remove('hidden');
    }

    setConfigurationLoading(message) {
        if (this.configStatus) {
            this.configStatus.textContent = message;
            this.configStatus.classList.remove('hidden');
        }
        this.configBody?.classList.add('hidden');
    }

    setConfigurationError(message) {
        if (this.configStatus) {
            this.configStatus.textContent = message;
            this.configStatus.classList.remove('hidden');
        }
        this.configBody?.classList.add('hidden');
    }

    bindEvents() {
        if (this.root.dataset.reportRunBound === 'true') {
            return;
        }
        this.root.dataset.reportRunBound = 'true';
        this.refreshButton?.addEventListener('click', () => this.loadReport());
    }

    startRefreshTimer() {
        const refreshSeconds = Number(this.root?.dataset?.refreshSeconds || 0);
        if (refreshSeconds <= 0) {
            return;
        }

        this.refreshTimer = window.setInterval(() => {
            if (document.body.contains(this.root)) {
                this.loadReport();
            } else {
                this.destroyWithin(this.root);
            }
        }, refreshSeconds * 1000);
    }

    async loadReport() {
        const endpoint = this.root?.dataset?.endpoint;
        if (!endpoint) {
            return;
        }

        this.setMeta('Running report');
        this.toggleState({ empty: false, error: false });

        try {
            const response = await fetch(endpoint, {
                headers: { Accept: 'application/json' },
                credentials: 'same-origin'
            });
            const payload = await response.json();
            if (!response.ok) {
                throw new Error(payload.error || 'Unable to run report');
            }

            this.type.textContent = payload.chartType || payload.reportType || 'run';

            if (payload.reportType === 'chart') {
                const labels = Array.isArray(payload?.data?.labels) ? payload.data.labels : [];
                const datasets = Array.isArray(payload?.data?.datasets) ? payload.data.datasets : [];
                if (!labels.length || !datasets.length) {
                    this.destroyChart();
                    this.renderTable(null);
                    this.toggleState({ empty: true, error: false });
                    this.setMeta(payload?.meta?.summaryText || 'No data returned for this report');
                    return;
                }
                this.renderChart(payload);
            } else {
                const rows = Array.isArray(payload?.data?.rows) ? payload.data.rows : [];
                if (!rows.length) {
                    this.destroyChart();
                    this.renderTable(payload);
                    this.toggleState({ empty: true, error: false });
                    this.setMeta(payload?.meta?.summaryText || 'No data returned for this report');
                    return;
                }
                this.destroyChart();
                this.renderTable(payload);
            }
            this.toggleState({ empty: false, error: false });
            this.setMeta(payload?.meta?.summaryText || 'Updated');
        } catch (error) {
            console.error('Report run failed', error);
            this.destroyChart();
            this.renderTable(null);
            this.toggleState({ empty: false, error: true, errorText: error.message || 'Unable to run report' });
            this.setMeta('Unable to refresh report');
        }
    }

    renderChart(payload) {
        if (!this.canvas || !this.ChartLibrary) {
            return;
        }

        this.showMode('chart');
        this.destroyChart();
        const chartType = payload.chartType || this.root.dataset.chartType || 'bar';
        const options = this.buildOptions(chartType, payload.options || {});
        const drilldownUrls = Array.isArray(payload?.meta?.drilldownUrls) ? payload.meta.drilldownUrls : [];
        this.chart = new this.ChartLibrary(this.canvas, {
            type: chartType,
            data: payload.data,
            options: this.deepMerge(options, {
                onClick: (_event, elements) => {
                    const index = elements?.[0]?.index;
                    const targetUrl = Number.isInteger(index) ? drilldownUrls[index] : '';
                    if (targetUrl) {
                        window.location.href = targetUrl;
                    }
                }
            })
        });

        this.canvas.style.cursor = drilldownUrls.length ? 'pointer' : 'default';
    }

    renderTable(payload) {
        if (!this.table) {
            return;
        }
        if (!payload || !Array.isArray(payload?.data?.columns) || !Array.isArray(payload?.data?.rows)) {
            this.table.innerHTML = '';
            return;
        }
        this.showMode('table');
        const columns = payload.data.columns;
        const rows = payload.data.rows;
        this.table.innerHTML = `
            <div class="overflow-x-auto">
                <table class="table table-pin-rows table-sm md:table-md">
                    <thead>
                        <tr>${columns.map((column) => `<th>${this.escape(this.humanize(column))}</th>`).join('')}</tr>
                    </thead>
                    <tbody>
                        ${rows.map((row) => `
                            <tr>
                                ${columns.map((column, index) => {
                                    const value = this.escape(row[column] || '');
                                    const recordUrl = row._recordUrl || '#';
                                    return index === 0 && row._recordUrl
                                        ? `<td><a href="${recordUrl}" class="link link-primary link-hover">${value}</a></td>`
                                        : `<td>${value}</td>`;
                                }).join('')}
                            </tr>
                        `).join('')}
                    </tbody>
                </table>
            </div>
        `;
    }

    buildOptions(chartType, payloadOptions) {
        const defaults = {
            responsive: true,
            maintainAspectRatio: false,
            animation: {
                duration: 450,
                easing: 'easeOutQuart'
            },
            plugins: {
                legend: {
                    display: true,
                    position: 'bottom',
                    labels: {
                        boxWidth: 12,
                        usePointStyle: true,
                        pointStyle: 'circle'
                    }
                },
                tooltip: {
                    padding: 12,
                    cornerRadius: 12,
                    backgroundColor: 'rgba(15, 23, 42, 0.92)',
                    titleColor: '#f8fafc',
                    bodyColor: '#e2e8f0'
                }
            },
            scales: chartType === 'pie' || chartType === 'doughnut'
                ? {}
                : {
                    x: {
                        grid: { display: false },
                        ticks: { color: '#64748b' }
                    },
                    y: {
                        beginAtZero: true,
                        grid: { color: 'rgba(148, 163, 184, 0.18)' },
                        ticks: { color: '#64748b' }
                    }
                }
        };

        return this.deepMerge(defaults, payloadOptions);
    }

    setMeta(text) {
        if (!this.meta) {
            return;
        }

        this.meta.innerHTML = '';

        const badge = document.createElement('span');
        badge.className = 'rounded-full bg-base-200 px-3 py-1';
        badge.textContent = text;
        this.meta.appendChild(badge);

        const updated = document.createElement('span');
        updated.className = 'rounded-full border border-base-300 px-3 py-1';
        updated.textContent = `Updated ${new Date().toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}`;
        this.meta.appendChild(updated);
    }

    toggleState({ empty = false, error = false, errorText = '' }) {
        this.empty?.classList.toggle('hidden', !empty);
        this.empty?.classList.toggle('flex', empty);

        if (this.error) {
            this.error.textContent = errorText;
            this.error.classList.toggle('hidden', !error);
            this.error.classList.toggle('flex', error);
        }
    }

    destroyWithin(root) {
        if (!root) {
            return;
        }
        if (this.refreshTimer) {
            window.clearInterval(this.refreshTimer);
            this.refreshTimer = null;
        }
        this.destroyChart();
    }

    destroyChart() {
        if (this.chart) {
            this.chart.destroy();
            this.chart = null;
        }
    }

    showMode(mode) {
        const chartMode = mode === 'chart';
        this.canvas?.classList.toggle('hidden', !chartMode);
        this.table?.classList.toggle('hidden', chartMode);
        if (this.visual) {
            this.visual.classList.toggle('h-auto', !chartMode);
            this.visual.classList.toggle('min-h-[18rem]', !chartMode);
        }
    }

    humanize(value) {
        return String(value || '').replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase());
    }

    prettyJson(value) {
        if (!value) {
            return '{}';
        }

        try {
            const parsed = typeof value === 'string' ? JSON.parse(value) : value;
            return JSON.stringify(parsed, null, 2);
        } catch (_error) {
            return String(value);
        }
    }

    escape(value) {
        return String(value)
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;')
            .replace(/'/g, '&#39;');
    }

    deepMerge(...sources) {
        const result = {};
        sources.forEach((source) => {
            if (!source || typeof source !== 'object' || Array.isArray(source)) {
                return;
            }
            Object.keys(source).forEach((key) => {
                const existingValue = result[key];
                const nextValue = source[key];
                if (this.isPlainObject(existingValue) && this.isPlainObject(nextValue)) {
                    result[key] = this.deepMerge(existingValue, nextValue);
                } else {
                    result[key] = nextValue;
                }
            });
        });
        return result;
    }

    isPlainObject(value) {
        return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
    }
}
